import { useCallback, useContext, createContext, FC, useEffect, useMemo, useReducer } from 'react';
import jwt_decode from 'jwt-decode';
import API from 'services/AxiosConfig';
import { userReducer, UserStateI, UserActionTypes, userInitialState } from 'store';
import { useCompanyActions } from 'hooks';
import { isAdmin, resetFilters } from 'lib';
import { toast } from 'react-toastify';

const STATE_LS_KEY = 'user';

interface AuthInput {
  email: string;
  password: string;
}

type AuthFn<T = void> = ({ email, password }: AuthInput) => Promise<T>;

interface decoded {
  id: string;
  fullName: string;
  email: string;
  userRole: [string];
  location: string;
}

export interface Context extends UserStateI {
  signInWithEmail: AuthFn;
  logout: () => void;
}

const getInitialState = (): UserStateI => {
  try {
    const storedState = localStorage.getItem(STATE_LS_KEY);
    return storedState ? JSON.parse(storedState) : userInitialState;
  } catch (error) {
    return userInitialState;
  }
};

export const AuthContext = createContext({} as Context);

export const AuthContextProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, undefined, getInitialState);
  const { setLocations, setUsers, resetCompany, setClients, setCarePartners, setCareManagerActivities, setApplicants, setAwardTypes, setDeactivationReasons, setLostClientReasons, setProspectlostClientReason, setApplicantStages, setReferralPartners, setCompanies, setProspect, setMilestones, setRatings, setCompanyRatings, setPriorityIndexes, setPriorityIndexesCP, setactivityTypes, setCompanyTypes, setApplicantsActivity, setLeadSources, setReferralActivityTypes, setApplicantActivityTypeCategory, setActivityCategoryManagement, setReferralPartnersActivityCategory, setProspectActivityCategory, setApplicantActivityType, setAuthorizationStages, setAuthorizationTypes, setReAuthorizationStages, setAuthorizations, setRequestedStages, setTags, setTagCategory, setprospectStages, setContactTypes, setContactRelationships, setApplicantSources, setDisciplinarySteps, setDisciplinaryCategories, setDisciplinaryAcks, setDisciplinaryAttendanceTypes, setApplicantThreshold } = useCompanyActions();

  const logout: Context['logout'] = useCallback(() => {
    dispatch({ type: UserActionTypes.LOGOUT });
    resetCompany();
    resetFilters();
    localStorage.removeItem('apexcare-token');
    localStorage.removeItem('user');
  }, [resetCompany]);

  const signInWithEmail: Context['signInWithEmail'] = useCallback(async ({ email, password }) => {
    try {
      const response = await API.post('/auth/login', { email, password });
      localStorage.setItem('apexcare-token', response.data.token);

      const decoded: decoded = jwt_decode(response.data.token);
      const { firstName, lastName, _id, roles } = response.data.user;

      const userData = {
        _id,
        decodedId: decoded.id,
        firstName,
        lastName,
        email: decoded.email,
        location: response.data.user.location,
        roles,
        profileImage: response.data.user.profileImage,
      };

      dispatch({ type: UserActionTypes.LOGIN, payload: userData });

      // Load company data based on role
      let locationData;
      if (!isAdmin(roles)) {
        const perms = await API.get(`permissions/${_id}`);
        const locIds = perms.data.data.map((p: any) => p.location._id).join(',');
        locationData = locIds ? await API.get(`location?locations=${locIds}`) : { data: { data: [] } };
      } else {
        locationData = await API.get('location/all?type=Active');
      }
      setLocations(locationData.data.data);
      localStorage.setItem('loginTime', new Date().toISOString());
      await Promise.all([
        API.get('user/Active').then(res => setUsers(res.data.data)),
        API.get('client/ddn/0').then(res => setClients(res.data.data)),
        API.get('care-partner/ddn/0').then(res => setCarePartners(res.data.data)),
        API.get('care-manager-activity').then(res => setCareManagerActivities(res.data.data)),
        API.get('applicants/ddn/0').then(res => setApplicants(res.data.data)),
        API.get('awards/ddn/Active').then(res => setAwardTypes(res.data.data)),
        API.get('/client/activity/search/Active').then(res => setDeactivationReasons(res.data.data)),
        API.get('/care-partner/activity/search/Active').then(res => setLostClientReasons(res.data.data)),
        API.get('/lost-client/activity/search/Active').then(res => setProspectlostClientReason(res.data.data)),
        API.get('applicants/stages').then(res => setApplicantStages(res.data.data)),
        API.get('referral-partners/ddn/0').then(res => setReferralPartners(res.data.data)),
        API.get('referral-partners/companies/ddn/0').then(res => setCompanies(res.data.data)),
        API.get('/prospects/ddn/all').then(res => setProspect(res.data.data)),
        API.get('/prospects/progress').then(res => setMilestones(res.data.data)),
        API.get('/rating-referral/activity/search/Active?referral=true').then(res => setRatings(res.data.data)),
        API.get('/rating-referral-company/activity/search/Active?referral=true').then(res => setCompanyRatings(res.data.data)),
        API.get('/priority-index/activity/search/Active?referral=true').then(res => setPriorityIndexes(res.data.data)),
        API.get('/priority-index-cp/activity/search/Active?referral=true').then(res => setPriorityIndexesCP(res.data.data)),
        API.get(`marketing/types?referral=false`).then(res => setactivityTypes(res.data.data)),
        API.get(`referral-partners/types/listing`).then(res => setCompanyTypes(res.data.data)),
        API.get('applicants/activity').then(res => setApplicantsActivity(res.data.data)),
        API.get('/lead-source/activity/search/Active').then(res => setLeadSources(res.data.data)),
        API.get('marketing/types?referral=true').then(res => setReferralActivityTypes(res.data.data)),
        API.get('location/all?type=Active').then(res => setLocations(res.data.data)),
        API.get('/applicant-activity-type/activity/search/Active?referral=true').then(res => setApplicantActivityType(res.data.data)),
        API.get('/applicant-activity-type-category/activity/search/Active?referral=true').then(res => setApplicantActivityTypeCategory(res.data.data)),
        API.get('/activity-category-management/activity/search/Active?referral=true').then(res => setActivityCategoryManagement(res.data.data)),
        API.get('/marketing-activity-type-category/activity/search/Active?referral=true').then(res => setReferralPartnersActivityCategory(res.data.data)),
        API.get('/marketing-activity-type-category/activity/search/Active?referral=false').then(res => setProspectActivityCategory(res.data.data)),
        API.get('/authorization/progress').then(res => setAuthorizationStages(res.data.data)),
        API.get('/reauthorization/progress').then(res => setReAuthorizationStages(res.data.data)),
        API.get('/requested/progress').then(res => setRequestedStages(res.data.data)),
        API.get('/authorization-types/activity/search/Active?referral=true').then(res => setAuthorizationTypes(res.data.data)),
        API.get('authorization/ddn/all').then(res => setAuthorizations(res.data.data)),
        API.get('tags/ddn/Active').then(res => setTags(res.data.data)),
        API.get('tag-category/activity/search/Active').then(res => setTagCategory(res.data.data)),
        API.get('/prospects/stages').then(res => setprospectStages(res.data.data)),
        API.get('/contact-type/activity/search/Active').then(res => setContactTypes(res.data.data)),
        API.get('/contact-relationships/activity/search/Active').then(res => setContactRelationships(res.data.data)),
        API.get('/applicants/sources').then(res => setApplicantSources(res.data.data)),
        API.get('/disciplinary-step/activity/search/Active').then(res => setDisciplinarySteps(res.data.data)),
        API.get('/disciplinary-category/activity/search/Active').then(res => setDisciplinaryCategories(res.data.data)),
        API.get('/disciplinary-ack/activity/search/Active').then(res => setDisciplinaryAcks(res.data.data)),
        API.get('/disciplinary-attendance-type/activity/search/Active').then(res => setDisciplinaryAttendanceTypes(res.data.data)),
        API.get('/applicant-threshold/activity/search/Active').then(res => setApplicantThreshold(res.data.data)),
      ]);

      localStorage.setItem('openActivities', JSON.stringify([]));

    } catch (error: any) {
      toast.error(error?.response?.data?.error);
    }
  }, [setActivityCategoryManagement, setApplicantActivityType, setApplicantActivityTypeCategory, setApplicantSources, setApplicantStages, setApplicantThreshold, setApplicants, setApplicantsActivity, setAuthorizations, setAuthorizationStages, setAuthorizationTypes, setAwardTypes, setCareManagerActivities, setCarePartners, setClients, setCompanies, setCompanyRatings, setCompanyTypes, setContactRelationships, setContactTypes, setDeactivationReasons, setDisciplinaryAcks, setDisciplinaryAttendanceTypes, setDisciplinaryCategories, setDisciplinarySteps, setLeadSources, setLocations, setLostClientReasons, setProspectlostClientReason, setMilestones, setPriorityIndexes, setPriorityIndexesCP, setProspect, setProspectActivityCategory, setRatings, setReAuthorizationStages, setReferralActivityTypes, setReferralPartners, setReferralPartnersActivityCategory, setRequestedStages, setTagCategory, setTags, setUsers, setactivityTypes, setprospectStages]);

  useEffect(() => {
    localStorage.setItem(STATE_LS_KEY, JSON.stringify(state));
  }, [state]);

  const value = useMemo(
    () => ({
      signInWithEmail,
      logout,
      ...state,
    }),
    [logout, signInWithEmail, state]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default function useAuth() {
  const context = useContext(AuthContext);

  return context;
}
